import React, { useMemo } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

interface LicenseStatusBadgeProps {
  /** Rozete tıklanınca çalışır (ödeme/lisans popup'ını açan taraf yönetir). */
  onClick: () => void;
}

/**
 * Demo / Lisans durum rozeti — hem ana panel Header'ında hem şube manager panelinde
 * kullanılır. `useAuth()` içindeki demoUntil/licenseUntil'den beslenir:
 *  - Demo aktifken: "Demo: X gün" (süre yaklaştıkça renk kızarır).
 *  - Demo değil + aktif lisans varsa: ">30 gün → Lisanslı Ürün", "≤30 gün → Lisans: X gün".
 *  - Hiçbiri yoksa: null (rozet gösterilmez).
 * Tıklama davranışı `onClick` ile dışarı bırakılır (Header → LicensePaymentDialog,
 * Manager → ManagerBillingDialog).
 */
const LicenseStatusBadge: React.FC<LicenseStatusBadgeProps> = ({ onClick }) => {
  const { t, i18n } = useTranslation();
  const { demoUntil, demoExpired, licenseUntil } = useAuth();

  // Demo: kalan gün istemcide hesaplanır; kilit kararı sunucunun demoExpired'ıdır.
  const demoInfo = useMemo(() => {
    if (!demoUntil) return null;
    if (demoExpired) return { daysRemaining: 0, expired: true, endDate: new Date(demoUntil) };
    const endDate = new Date(demoUntil);
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000));
    return { daysRemaining, expired: false, endDate };
  }, [demoUntil, demoExpired]);

  // Lisans: demo AKTİF DEĞİLKEN ve aktif sözleşme (licenseUntil) varsa.
  const licenseInfo = useMemo(() => {
    if (demoUntil) return null;
    if (!licenseUntil) return null;
    const endDate = new Date(licenseUntil);
    const daysRemaining = Math.ceil((endDate.getTime() - Date.now()) / 86400000);
    return { daysRemaining, endDate, expired: daysRemaining < 0 };
  }, [demoUntil, licenseUntil]);

  if (demoInfo) {
    // Son 3 gün kırmızı, son hafta turuncu: süre yaklaştıkça göze çarpsın.
    const color = demoInfo.expired || demoInfo.daysRemaining <= 3
      ? 'error'
      : demoInfo.daysRemaining <= 7 ? 'warning' : 'info';
    const fullLabel = demoInfo.expired
      ? t('header.demoExpired')
      : demoInfo.daysRemaining <= 1
        ? t('header.demoLastDay')
        : t('header.demoRemaining', { count: demoInfo.daysRemaining });
    const shortLabel = demoInfo.expired
      ? t('header.demoExpiredShort')
      : demoInfo.daysRemaining <= 1
        ? t('header.demoLastDayShort')
        : t('header.demoRemainingShort', { count: demoInfo.daysRemaining });

    return (
      <Tooltip title={t('header.payTooltip')}>
        <Chip
          size="small"
          color={color}
          icon={<HourglassBottomIcon />}
          clickable
          onClick={onClick}
          label={
            <>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{fullLabel}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{shortLabel}</Box>
            </>
          }
          sx={{ fontWeight: 700, height: 28, cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  if (licenseInfo) {
    const daysLeft = licenseInfo.daysRemaining;
    const nearDue = daysLeft <= 30;
    const color = licenseInfo.expired || daysLeft <= 3
      ? 'error'
      : daysLeft <= 7 ? 'warning' : nearDue ? 'warning' : 'success';
    // >30 gün: "Lisanslı Ürün". ≤30 gün: "Lisans: X gün kaldı".
    const fullLabel = nearDue
      ? (daysLeft <= 1
        ? t('header.licenseLastDay')
        : t('header.licenseRemaining', { count: Math.max(0, daysLeft) }))
      : t('header.licensedProduct');
    const shortLabel = nearDue
      ? (daysLeft <= 1 ? t('header.demoLastDayShort') : t('header.demoRemainingShort', { count: Math.max(0, daysLeft) }))
      : t('header.licensedProductShort');

    return (
      <Tooltip title={t('header.payTooltip')}>
        <Chip
          size="small"
          color={color}
          icon={nearDue ? <HourglassBottomIcon /> : <VerifiedIcon />}
          clickable
          onClick={onClick}
          label={
            <>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{fullLabel}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{shortLabel}</Box>
            </>
          }
          sx={{ fontWeight: 700, height: 28, cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  return null;
};

export default LicenseStatusBadge;
